<template>
  <div>
    <div class="tipslista__filter">
      <Spinner v-if="isLoadingFilter" />
      <tips-filter
        v-if="config"
        :config="config"
        v-model:freeText="freeText"
        :selected-values="selectedValues"
        @submit="submitFilter"
        @reset="resetFilters"
      />
    </div>

    <div>
      <Spinner v-if="isLoadingResults" />
      <div v-else-if="resultsError" class="alert alert-danger" role="alert">
        <strong>Något gick fel.</strong> Det gick inte att hämta resultaten. Försök igen senare.
      </div>
      <template v-else-if="results">
        <tips-list :items="results.Items" :total-count="results.TotalCount" />
        <tips-pagination
          v-if="results.PageCount > 1"
          :current-page="results.CurrentPage"
          :page-count="results.PageCount"
          @page-change="goToPage"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref } from 'vue';
import TipsFilter from './TipsFilter.vue';
import TipsList from './TipsList.vue';
import TipsPagination from '../components/Pagination.vue';
import { FilterConfigDto, TipsResultsDto, fetchFilterConfig, fetchResults } from './hittaTipsSidaService';
import Spinner from '../components/spinner.vue';
import { useRoute, useRouter } from 'vue-router'
import { watch } from 'vue'

export default defineComponent({
  name: 'HittaTipsSida',
  components: { TipsFilter, TipsList, TipsPagination, Spinner },
  props: {
    contentId: { type: Number, required: true },
  },
  setup(props) {
    const config = ref<FilterConfigDto | null>(null);
    const results = ref<TipsResultsDto | null>(null);
    const isLoadingFilter = ref(true);
    const isLoadingResults = ref(true);
    const resultsError = ref(false);
    const freeText = ref('');
    const selectedValues = reactive<Record<string, string[]>>({});
    const currentPage = ref(1);
    const sharedId = ref<string | null>(null);
    let resultsAbortController: AbortController | null = null;
    const route = useRoute();
    const router = useRouter();

    watch(
      () => route.query,
      () => {
        loadResults();
      },
      { deep: true }
    )

    function initFromUrl() {
      // Normalize keys to lowercase — shared links use PascalCase (e.g. Fsq, No, Typ, Id)
      // because the backend generates them via nameof(HittaTipsSidaQuery.X).
      const normalized = new URLSearchParams();
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        normalized.append(key.toLowerCase(), value);
      }

      freeText.value = normalized.get('fsq') ?? '';
      sharedId.value = normalized.get('id');
      currentPage.value = parseInt(normalized.get('page') ?? '1', 10) || 1;
      for (const key of normalized.keys()) {
        if (key !== 'fsq' && key !== 'page' && key !== 'id' && key !== 'contentid') {
          selectedValues[key] = normalized.getAll(key);
        }
      }
    }

    function buildQueryParams(): URLSearchParams {
      const params = new URLSearchParams();
      if (freeText.value) params.set('fsq', freeText.value);
      for (const [key, values] of Object.entries(selectedValues)) {
        values.forEach(v => params.append(key, v));
      }
      return params;
    }

    function pushUrlState() {
      router.push({ path: route.path, query: {...selectedValues, fsq: freeText.value, page: currentPage.value} });
    }

    async function loadFilterConfig() {
      try {
        const data = await fetchFilterConfig(props.contentId);
        config.value = data;
        data.FilterElements.forEach((el) => {
          if (!selectedValues[el.QueryStringParameter]) {
            selectedValues[el.QueryStringParameter] = [];
          }
        });
      } finally {
        isLoadingFilter.value = false;
      }
    }

    async function loadResults() {
      resultsAbortController?.abort();
      resultsAbortController = new AbortController();

      isLoadingResults.value = true;
      resultsError.value = false;
      try {
        const params = buildQueryParams();
        if (currentPage.value > 1) params.set('page', String(currentPage.value));
        if (sharedId.value) params.set('id', sharedId.value);
        results.value = await fetchResults(props.contentId, params, resultsAbortController.signal);
        isLoadingResults.value = false;
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          resultsError.value = true;
          isLoadingResults.value = false;
        }
      }
    }

    function submitFilter() {
      currentPage.value = 1;
      pushUrlState();
    }

    function resetFilters() {
      freeText.value = '';
      sharedId.value = null;
      Object.keys(selectedValues).forEach((key) => { selectedValues[key] = []; });
      currentPage.value = 1;
      pushUrlState();
    }

    function goToPage(page: number) {
      if (!results.value || page < 1 || page > results.value.PageCount) return;
      currentPage.value = page;
      pushUrlState();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onMounted(() => {
      initFromUrl();
      loadFilterConfig();
      loadResults();
    });

    return {
      config,
      results,
      isLoadingFilter,
      isLoadingResults,
      resultsError,
      freeText,
      selectedValues,
      submitFilter,
      resetFilters,
      goToPage,
    };
  },
});
</script>
